import type { ExportNotesSection } from '../schemas/export-notes.js';
import type {
  ExportAppearanceOptions,
  ExportDressingOptions,
  ExportSelection,
  ExportTableOptions,
} from '../schemas/export.js';
import type { Noyau } from '../schemas/noyau.js';
import type { ExportModel } from './build-export-model.js';
import type { TilePage } from './compute-tile-pages.js';
import {
  HEADER_META_PT,
  documentFooterEnabled,
  documentHeaderHeightPx,
  documentHeaderVisibility,
} from './document-header.js';
import {
  type ExportAnnotation,
  type ProjectedInk,
  highlightedLineKeys,
  projectInkAnnotations,
} from './export-annotations.js';
import {
  CSS_PX_PER_PT,
  DEFAULT_ROW_HEIGHT_PX,
  DENSITY_PADDING_PX,
  FOOTER_GAP_PX,
  FOOTER_RESERVED_PX,
  HEADER_ROW_RESERVED_PX,
  LINE_HEIGHT_FACTOR,
  type PageGeometry,
  ROW_LABEL_WIDTH_PX,
  SUBJECT_DESCRIPTION_RESERVED_PX,
} from './page-geometry.js';
import { renderContinuousExportHtml } from './render-export-html-continuous.js';
import {
  BORDER_COLOR,
  BORDER_STYLE,
  DOCUMENT_HEADER_CSS,
  escapeHtml,
  formatPx,
  pmsSignatureHtml,
  renderColumnHeaderRow,
  renderDataCells,
  renderDocumentHeaderContent,
  renderGridColgroup,
  renderHtmlDocument,
  renderRowLabelCells,
  renderSubjectDescriptionBlock,
  renderTransposedSectionHtml,
} from './render-export-html-parts.js';
import {
  type ExportNotesBlock,
  notesStyle,
  planNotesPages,
  renderNotesBlocksHtml,
  renderNotesTitleHtml,
} from './render-export-notes.js';
import { TITILLIUM_FAMILY } from './titillium-font-face.js';
import {
  type TransposedSection,
  buildTransposedSections,
  computeTransposedPages,
  transposedUsableHeightPx,
} from './transposed.js';

/**
 * The footer box's pinned height: the reservation minus its gap above, so the
 * footer can never grow into the grid rows the tiling kept clear of it.
 */
const FOOTER_MARK_HEIGHT_PX = FOOTER_RESERVED_PX - FOOTER_GAP_PX;

export interface RenderExportHtmlInput {
  model: ExportModel;
  tiles: TilePage[];
  matrixTitle: string;
  companyName: string;
  /** null when the caller never offers this block (excel never calls this function at all; kept for type-safety at call sites that always pass an object for html/pdf). */
  subjectDescription: {
    include: boolean;
    position: 'before' | 'after';
    sujet: string | null;
    contexte: string | null;
  } | null;
  /** How the six activation rows are labelled — see `exportSelectionSchema`. */
  activationLevierNames: ExportSelection['activationLevierNames'];
  /** 1, or 2 in `column` mode where a dedicated levier-name column sits beside the spanning group label. Must match what the tiling reserved. */
  labelColumnCount: 1 | 2;
  /** Raw (unscaled) per-column pixel widths, keyed by palier number as a string — `matrix.viewState.columnWidths` (decision 10, 2026-08-09 doc). Missing entries fall back to `DEFAULT_COLUMN_WIDTH_PX`. Each tile's `scalePercent` is applied on top before rendering. */
  columnWidths: Record<string, number>;
  /** Raw (unscaled) per-row pixel heights, keyed by `${row.kind}:${row.key}` — same source and shape the tiling already used for its math (`matrix.viewState.rowHeights`). Missing entries fall back to `DEFAULT_ROW_HEIGHT_PX`. Each tile's `scalePercent` is applied on top before rendering. */
  rowHeights: Record<string, number>;
  /**
   * The physical page this document is laid out for (spec R1). Each
   * `.export-page` becomes a box of exactly the *content* area — the page minus
   * its margins — because Chromium owns the physical margins via Playwright's
   * `page.pdf({ margin })`. Fixed boxes are what let the footer sit at the true
   * bottom of every page instead of trailing the last row of the grid.
   */
  geometry: PageGeometry;
  /**
   * The company's own noyau — the end of the `noyau-gradient` theme's ramp,
   * unread by the other two themes (issue #8). `null` leaves those headers
   * unpainted rather than guessing a colour.
   */
  noyau: Noyau | null;
  /** Base table font size in points (spec R1). Converted to CSS px by `CSS_PX_PER_PT` and then scaled by each tile's `scalePercent`. */
  fontSizePt: number;
  /** Density, row-height mode, repeated headers, structure (spec R2). */
  table: ExportTableOptions;
  /** Logo / title block / dates / footer / PMS signature / assembly marks (spec R4). */
  dressing: ExportDressingOptions;
  /** Palier header theme, border weight, zebra striping (spec R5). */
  appearance: ExportAppearanceOptions;
  /** Data URIs supplied by the caller — the backend embeds bytes, the browser preview passes bundled URLs. `null` means "no such asset", which removes the element rather than leaving a broken image. */
  companyLogoDataUri: string | null;
  pmsLogoHorizontalDataUri: string | null;
  pmsLogoRingDataUri: string | null;
  /** Pre-formatted display dates for the header's dates block (spec R4). `null` hides the corresponding line; both null hides the block even when `dressing.dates` is on. */
  exportedAt: string | null;
  matrixUpdatedAt: string | null;
  /**
   * The document's `@font-face` block (decision F2). Supplied by the caller
   * because only the caller knows how its environment can reach the font
   * bytes — bundled URLs in the browser, base64 `data:` URIs in the backend,
   * which renders on `about:blank` where no path resolves. Build it with
   * `buildTitilliumFontFace`. Omitted means no embedded faces: the document
   * still asks for Titillium and gets it if the host happens to have it,
   * otherwise the fallback stack.
   */
  fontFaceCss?: string;
  /**
   * Content-anchored annotations (decisions A1-A3), baked into the document
   * here (decision A9) rather than applied to a rendered artifact afterwards.
   * Highlights become a background on their line; ink strokes become an SVG
   * overlay per tile. The continuous layout keeps the highlights and drops the
   * ink (decision X8, issue #12).
   */
  annotations?: readonly ExportAnnotation[];
  /**
   * `paged` (the default) is the fixed-page-box document the PDF is printed
   * from. `continuous` is the interactive HTML export (issue #12): one
   * unpaginated page with sticky palier headers and row labels, rendered by
   * `renderContinuousExportHtml`, which documents which of the fields above it
   * reads and which it ignores (`tiles`, `repeatHeaders`, page numbers,
   * assembly marks, ink).
   */
  layout?: 'paged' | 'continuous';
  /**
   * The « Notes » section (issue #19): the memo and the open Client
   * discussions, built by `buildExportNotes`. The paged document ends with its
   * pages (counted in "Page x sur N" and in the plan's `notesPageCount`); the
   * continuous one with a section. `null`/absent leaves the document exactly
   * what it was.
   */
  notes?: ExportNotesSection | null;
}

/**
 * Annotation state derived once per document and shared by every tile —
 * `highlightedLineKeys` and `projectInkAnnotations` both walk the whole
 * annotation list, so computing them inside `renderTile` would repeat that
 * walk once per tile (a 12-tile export would do it twelve times).
 */
interface AnnotationLayers {
  highlightKeys: Set<string>;
  inkByTileIndex: Map<number, ProjectedInk[]>;
}

function buildAnnotationLayers(input: RenderExportHtmlInput): AnnotationLayers {
  const annotations = input.annotations ?? [];
  const inkByTileIndex = new Map<number, ProjectedInk[]>();
  for (const projected of projectInkAnnotations({
    annotations,
    model: input.model,
    tiles: input.tiles,
    columnWidths: input.columnWidths,
    rowHeights: input.rowHeights,
  })) {
    const existing = inkByTileIndex.get(projected.tileIndex);
    if (existing === undefined) inkByTileIndex.set(projected.tileIndex, [projected]);
    else existing.push(projected);
  }
  return { highlightKeys: highlightedLineKeys(annotations), inkByTileIndex };
}

/**
 * Document-wide facts every page box needs, derived once. The header height in
 * particular is read twice per document — by the first page's `<header>` and by
 * every top-strip continuation page's reserved band (spec D3) — and both must
 * be the *same* number or the grid rows stop resuming at the same Y across a
 * horizontal join.
 */
interface DocumentContext {
  layers: AnnotationLayers;
  headerHeightPx: number;
  headerHtml: string;
  /** `${rowPageIndex}:${columnPageIndex}` for every tile — lets an assembly mark ask whether a page continues to its right without rescanning the tile list. */
  tileKeys: Set<string>;
  /**
   * Total number of physical pages the document renders — `input.tiles.length`
   * for the 'colonnes' structure, or the transposed page count for 'transpose'
   * (which ignores `input.tiles`). Used for pagination text ("Page x sur N")
   * and the single-page special case, so callers don't have to know which
   * structure produced the page they're rendering.
   */
  totalPages: number;
  /** The pages of the grid alone - what `totalPages` is before the notes pages follow them. */
  gridPageCount: number;
  /** Spec R4: the signature moves out of the footer and into the last page's bottom-right corner in assembly-marks (Atelier) mode. */
  signatureInCorner: boolean;
  footerEnabled: boolean;
  /** `dressing.footerText` normalised — a blank string is no footer text at all, not an empty left slot. */
  footerText: string | null;
}

/** Pinned to the height the tiling reserved for it — see `DocumentContext.headerHeightPx`. */
function renderDocumentHeader(input: RenderExportHtmlInput, heightPx: number): string {
  if (heightPx === 0) return '';
  return `<header class="export-header" style="height:${heightPx}px">${renderDocumentHeaderContent(input)}</header>`;
}

/**
 * Spec D3: « A1 → / ← A2 » in the corner, so a pasted-up poster can be
 * assembled without guessing. Row strips are lettered, column pages numbered;
 * the arrows point at the joins this page actually has.
 */
function rowStripLetter(rowPageIndex: number): string {
  let remaining = rowPageIndex;
  let letters = '';
  do {
    letters = String.fromCharCode(65 + (remaining % 26)) + letters;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return letters;
}

function renderAssemblyMark(
  input: RenderExportHtmlInput,
  tile: TilePage,
  context: DocumentContext,
): string {
  if (!input.dressing.assemblyMarks) return '';
  const label = `${rowStripLetter(tile.rowPageIndex)}${tile.columnPageIndex + 1}`;
  const hasLeft = tile.columnPageIndex > 0;
  const hasRight = context.tileKeys.has(`${tile.rowPageIndex}:${tile.columnPageIndex + 1}`);
  return `<div class="export-assembly-mark">${hasLeft ? '← ' : ''}${label}${hasRight ? ' →' : ''}</div>`;
}

function renderFooter(
  input: RenderExportHtmlInput,
  tileIndex: number,
  context: DocumentContext,
): string {
  if (!context.footerEnabled) return '';
  const left = context.footerText === null ? '' : `<span>${escapeHtml(context.footerText)}</span>`;
  // A single page is not "page 1 of 1" — it is just the document.
  const pagination =
    input.dressing.pageNumbers && context.totalPages > 1
      ? `<span>Page ${tileIndex + 1} sur ${context.totalPages}</span>`
      : '';
  const signature = context.signatureInCorner ? '' : pmsSignatureHtml(input);
  return `<footer class="export-footer"><div class="export-footer-left">${left}</div><div class="export-footer-center">${pagination}</div><div class="export-footer-right">${signature}</div></footer>`;
}

/** Shared by both the grid tile and the transposed page: the signature moves
 *  here in assembly-marks mode (spec R4), on the last physical page only. */
function renderCornerSignature(
  input: RenderExportHtmlInput,
  context: DocumentContext,
  isLastPage: boolean,
): string {
  if (!context.signatureInCorner || !isLastPage) return '';
  return `<div class="export-corner-signature"><span>Généré avec</span>${
    input.pmsLogoRingDataUri === null
      ? ''
      : `<img src="${escapeHtml(input.pmsLogoRingDataUri)}" alt="patrickmathieu singularité" />`
  }</div>`;
}

function renderTile(
  input: RenderExportHtmlInput,
  tile: TilePage,
  tileIndex: number,
  context: DocumentContext,
): string {
  const { model, columnWidths, rowHeights, activationLevierNames, labelColumnCount } = input;
  const repeatHeaders = input.table.repeatHeaders;
  const showColumnHeaders = repeatHeaders || tile.rowPageIndex === 0;
  const showRowLabels = repeatHeaders || tile.columnPageIndex === 0;
  const columns = tile.columnIndices
    .map((index) => model.columns[index])
    .filter((column): column is ExportModel['columns'][number] => column !== undefined);
  const rows = tile.rowIndices
    .map((index) => model.rows[index])
    .filter((row): row is ExportModel['rows'][number] => row !== undefined);
  const scale = tile.scalePercent / 100;
  const rowLabelWidthPx = Math.round(ROW_LABEL_WIDTH_PX * scale);
  const { highlightKeys } = context.layers;

  const colgroup = renderGridColgroup(columns, columnWidths, labelColumnCount, scale);

  const headerRow = showColumnHeaders
    ? renderColumnHeaderRow(columns, labelColumnCount, input.appearance, input.noyau)
    : '';

  // The bin-packing splits rows at arbitrary indices, so the six-row activation
  // block can straddle a page boundary. The group label is therefore re-emitted
  // on every tile that carries any of it, spanning only the rows actually
  // present *here* — not the full six. Model rows put the activation block
  // first and `tile.rowIndices` are contiguous, so the activation rows of a
  // tile are a contiguous prefix of `rows`.
  const activationInTile = rows.filter((row) => row.kind === 'activation');
  const firstActivationIndex = rows.findIndex((row) => row.kind === 'activation');

  const bodyRows = rows
    .map((row, index) => {
      const heightPx = Math.round(
        (rowHeights[`${row.kind}:${row.key}`] ?? DEFAULT_ROW_HEIGHT_PX) * scale,
      );
      const rowLabelCell = renderRowLabelCells({
        row,
        isFirstActivationRow: index === firstActivationIndex,
        activationRowCount: activationInTile.length,
        activationLevierNames,
        labelColumnCount,
        showRowLabels,
      });
      const cells = renderDataCells(model, row, columns, highlightKeys);
      return `<tr style="height:${heightPx}px">${rowLabelCell}${cells}</tr>`;
    })
    .join('');

  // Decision A9 / A2: ink strokes are drawn as an SVG overlay sitting on the
  // tile's content area. The offset is fully deterministic — label columns are
  // a fixed width, and `th.export-col-header` is pinned to
  // HEADER_ROW_RESERVED_PX (the same value the bin-packing already reserves,
  // so reservation and reality now agree). The overlay is anchored to
  // `.export-page-content`, not to the page box: the document header, the
  // reserved band and a "before" subject block all sit *outside* that wrapper,
  // so the stroke geometry stays independent of the dressing above it. Only
  // strokes anchored to a cell on *this* tile are emitted; the rest belong to
  // other tiles or were dropped with content that left the export.
  const inkForTile = context.layers.inkByTileIndex.get(tileIndex) ?? [];

  const inkLeftPx = labelColumnCount * rowLabelWidthPx;
  const inkTopPx = showColumnHeaders ? Math.round(HEADER_ROW_RESERVED_PX * scale) : 0;
  const inkLayer =
    inkForTile.length === 0
      ? ''
      : `<svg class="export-ink-layer" style="left:${inkLeftPx}px;top:${inkTopPx}px" aria-hidden="true">${inkForTile
          .map(
            (projected) =>
              `<polyline points="${projected.points
                .map((point) => `${Math.round(point.x)},${Math.round(point.y)}`)
                .join(' ')}" />`,
          )
          .join('')}</svg>`;

  const isFirstPage = tileIndex === 0;
  // The grid's last page: where an « après le tableau » sujet goes. The corner
  // signature goes on the document's last page, which the notes pages may follow.
  const isLastPage = tileIndex === input.tiles.length - 1;
  const subjectDescriptionHtml = renderSubjectDescriptionBlock(input.subjectDescription);
  const position = input.subjectDescription?.position;
  const before = isFirstPage && position === 'before' ? subjectDescriptionHtml : '';
  const after = isLastPage && position === 'after' ? subjectDescriptionHtml : '';

  // Spec D3: the header exists only on the top-left page, so every *other*
  // page of the same row strip reserves an empty band of the same height —
  // that is what makes each grid row resume at the same Y across the join.
  // Following row strips reserve nothing.
  const band =
    tile.rowPageIndex === 0 && tile.columnPageIndex > 0
      ? `<div class="export-band" style="height:${context.headerHeightPx}px"></div>`
      : '';

  const cornerSignature = renderCornerSignature(
    input,
    context,
    tileIndex === context.totalPages - 1,
  );

  const tableFontPx = formatPx(input.fontSizePt * CSS_PX_PER_PT * scale);

  // Decision A4: published rather than inferred — the annotation overlay needs
  // the tile scale to turn CSS pixels into the unscaled grid pixels a stroke
  // stores.
  return `<section class="export-page" data-scale-percent="${tile.scalePercent}">
    ${isFirstPage ? context.headerHtml : ''}${band}${before}
    <div class="export-page-content">
      ${inkLayer}
      <table class="export-table" style="font-size:${tableFontPx}px">
        ${colgroup}
        <thead>${headerRow}</thead>
        <tbody>${bodyRows}</tbody>
      </table>
    </div>
    ${after}${renderFooter(input, tileIndex, context)}${renderAssemblyMark(input, tile, context)}${cornerSignature}
  </section>`;
}

/**
 * A transposed structure's physical page (spec R2/P2 Dossier): the same fixed
 * page box, header/footer/dressing/subject-description rules as `renderTile`
 * (Task 7), but the content is a vertical stack of `TransposedSection` tables
 * instead of one grid table — a single column strip, so unlike `renderTile`
 * there is never a reserved band (that only exists to keep grid rows aligned
 * across a *column* join, and a transposed export has no column axis) and
 * there is no ink-annotation overlay (ink anchors assume the grid's row/column
 * geometry, which the transposed layout doesn't have).
 */
function renderTransposedPage(
  input: RenderExportHtmlInput,
  sections: readonly TransposedSection[],
  page: { sectionIndices: number[] },
  pageIndex: number,
  context: DocumentContext,
): string {
  const isFirstPage = pageIndex === 0;
  const isLastPage = pageIndex === context.gridPageCount - 1;
  const { highlightKeys } = context.layers;

  const sectionsHtml = page.sectionIndices
    .map((index) => sections[index])
    .filter((section): section is TransposedSection => section !== undefined)
    .map((section) =>
      renderTransposedSectionHtml(section, input.appearance, input.noyau, highlightKeys, 'body'),
    )
    .join('');

  const subjectDescriptionHtml = renderSubjectDescriptionBlock(input.subjectDescription);
  const position = input.subjectDescription?.position;
  const before = isFirstPage && position === 'before' ? subjectDescriptionHtml : '';
  const after = isLastPage && position === 'after' ? subjectDescriptionHtml : '';

  const tableFontPx = formatPx(input.fontSizePt * CSS_PX_PER_PT);

  // A pseudo tile purely to reuse `renderAssemblyMark`'s letter/join-arrow
  // logic. `columnPageIndex` is always 0 (spec/brief: a transposed export
  // never has a column axis), so `hasRight` there is always false — no join
  // arrow ever fires, which is correct: there is nothing to its right.
  const pseudoTile: TilePage = {
    columnIndices: [],
    rowIndices: [],
    columnPageIndex: 0,
    rowPageIndex: pageIndex,
    scalePercent: 100,
  };

  return `<section class="export-page">
    ${isFirstPage ? context.headerHtml : ''}${before}
    <div class="export-page-content" style="font-size:${tableFontPx}px">
      ${sectionsHtml}
    </div>
    ${after}${renderFooter(input, pageIndex, context)}${renderAssemblyMark(input, pseudoTile, context)}${renderCornerSignature(input, context, pageIndex === context.totalPages - 1)}
  </section>`;
}

/**
 * A page of the notes section (issue #19), after the grid's own pages: the same
 * fixed page box and footer, a title and the blocks the plan packed into it. No
 * document header (the first page carries it), no assembly mark (there is no
 * grid to join) and no ink.
 */
function renderNotesPage(
  input: RenderExportHtmlInput,
  blocks: readonly ExportNotesBlock[],
  notesPageIndex: number,
  pageIndex: number,
  context: DocumentContext,
): string {
  const isLastPage = pageIndex === context.totalPages - 1;
  return `<section class="export-page">
    <div class="export-page-content">
      ${renderNotesTitleHtml(notesPageIndex > 0)}${renderNotesBlocksHtml(blocks)}
    </div>
    ${renderFooter(input, pageIndex, context)}${renderCornerSignature(input, context, isLastPage)}
  </section>`;
}

/**
 * Decision F4: parity with the matrix grid on colour, typography and borders.
 * Cell padding is no longer a fixed 8px — spec R2 makes it the user's density
 * choice — but it is still uniform across the document, because row heights
 * come from `viewState.rowHeights` and a padding that varied per cell would
 * leave dead whitespace or clip text in a document that cannot be scrolled.
 *
 * The column header is the `.export-col-label` span alone: issue #16 removed
 * the palier number that used to sit above it (decision F1's two-tier header),
 * so the label carries the header on its own, bold at 1.1em in black, like the
 * grid.
 *
 * Decision S5: every vertical reservation is pinned, not merely reserved, so
 * that `pdfPointToCellAnchor` describes the page Chromium actually produced.
 * The body margin is part of that — browsers default it to 8px, which the
 * mapping does not model and which would offset every cell on every page.
 * `overflow: hidden` is what makes a pin a pin: content that overflowed would
 * push the grid down and desynchronise the mapping instead of clipping.
 */
function baseStyle(input: RenderExportHtmlInput, context: DocumentContext): string {
  const boxWidthPx = input.geometry.pageWidthPx - 2 * input.geometry.marginPx;
  const boxHeightPx = input.geometry.pageHeightPx - 2 * input.geometry.marginPx;
  const paddingPx = DENSITY_PADDING_PX[input.table.density];
  const border = BORDER_STYLE[input.appearance.borders];
  const borderColor = BORDER_COLOR[input.appearance.borders];
  // Spec R5 — striping is off by default; the rule is absent, not overridden,
  // so nothing has to un-paint a background the user never asked for.
  const zebra = input.appearance.zebra
    ? ' table.export-table tbody tr:nth-child(even) td { background: #faf9f7; }'
    : '';
  // Each dressing element's rules are emitted only when that element is in the
  // document. Spec R4 makes every one of them individually switchable, and a
  // stylesheet describing furniture the page does not have is both noise when
  // debugging a render and a false positive for anything inspecting the output.
  const headerStyle = context.headerHeightPx === 0 ? '' : DOCUMENT_HEADER_CSS;
  const footerStyle = !context.footerEnabled
    ? ''
    : `
  .export-footer { position: absolute; left: 0; right: 0; bottom: 0; height: ${FOOTER_MARK_HEIGHT_PX}px; overflow: hidden; display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: ${HEADER_META_PT}pt; color: #6b6a67; }
  .export-footer-right { display: flex; align-items: center; gap: 4px; }
  .export-footer img { display: block; height: ${FOOTER_MARK_HEIGHT_PX}px; width: auto; }`;
  const assemblyMarkStyle = !input.dressing.assemblyMarks
    ? ''
    : `
  .export-assembly-mark { position: absolute; top: 0; right: 0; font-size: 7px; letter-spacing: 0.08em; color: #b3aea8; }`;
  const cornerSignatureStyle = !context.signatureInCorner
    ? ''
    : `
  .export-corner-signature { position: absolute; right: 0; bottom: 0; display: flex; align-items: center; gap: 4px; font-size: 7px; color: #b3aea8; }
  .export-corner-signature img { display: block; height: 16px; width: auto; }`;
  return `
  body { margin: 0; font-family: '${TITILLIUM_FAMILY}', 'Segoe UI', system-ui, -apple-system, sans-serif; }
  .export-page { position: relative; box-sizing: border-box; overflow: hidden; width:${boxWidthPx}px; height:${boxHeightPx}px; }
  .export-page-content { position: relative; }
  .export-subject-description { height: ${SUBJECT_DESCRIPTION_RESERVED_PX}px; overflow: hidden; box-sizing: border-box; }${headerStyle}${footerStyle}${assemblyMarkStyle}${cornerSignatureStyle}
  table.export-table { border-collapse: collapse; table-layout: fixed; line-height: ${LINE_HEIGHT_FACTOR}; }
  table.export-transposed { border-collapse: collapse; table-layout: fixed; line-height: ${LINE_HEIGHT_FACTOR}; width: 100%; }
  table.export-table th, table.export-table td, table.export-transposed th, table.export-transposed td { border: ${border}; padding: ${paddingPx}px; text-align: left; vertical-align: top; }${zebra}
  th.export-col-header { border-bottom: 2px solid ${borderColor}; height: ${HEADER_ROW_RESERVED_PX}px; }
  th.export-transposed-title { background: #f7f6f4; font-weight: 600; }
  th.export-transposed-palier { background: #f7f6f4; }
  .export-ink-layer { position: absolute; overflow: visible; pointer-events: none; z-index: 2; }
  .export-ink-layer polyline { fill: none; stroke: #d13c3c; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
  .export-line-highlight { background: #f7e08a; }
  th.export-corner { background: #f7f6f4; }
  th.export-row-label { background: #f7f6f4; font-weight: 600; }
  th.export-row-label-hidden { visibility: hidden; }
  th.export-levier-label { background: #f7f6f4; font-weight: 400; }
  .export-col-label { display: block; font-size: 1.1em; font-weight: 700; line-height: 1.25; color: #000000; }
  .export-empty { color: #9b9693; font-style: italic; }
  .export-page td li { display: list-item; list-style: disc; margin-left: 1.25em; }
  .export-page td p { margin: 0; }
  @media screen { .export-page { height: auto; min-height:${boxHeightPx}px; overflow: visible; } }
  @media print { .export-page { break-after: page; } .export-page:last-child { break-after: auto; } }
`.replace(/\s+/g, ' ');
}

const SUBJECT_DESCRIPTION_STYLE = '.export-subject-description { margin-bottom: 16px; }';

export function renderExportHtml(input: RenderExportHtmlInput): string {
  if (input.layout === 'continuous') return renderContinuousExportHtml(input);

  const contentWidthPx = input.geometry.pageWidthPx - 2 * input.geometry.marginPx;
  const visibility = documentHeaderVisibility({
    dressing: input.dressing,
    hasCompanyLogo: input.companyLogoDataUri !== null,
    exportedAt: input.exportedAt,
    matrixUpdatedAt: input.matrixUpdatedAt,
  });
  // Spec D1/D4: the band is as tall as its content needs, capped at two title
  // lines — no more fixed 90px block amputating legitimate titles.
  const headerHeightPx = documentHeaderHeightPx({
    title: input.matrixTitle,
    contentWidthPx,
    ...visibility,
  });

  // Spec R4: in assembly-marks (Atelier) mode the signature leaves the footer
  // for the last page's bottom-right corner — which is why a signature alone
  // no longer keeps the footer alive there (`documentFooterEnabled`).
  const signatureInCorner =
    input.dressing.assemblyMarks && input.dressing.pmsSignature !== 'aucune';
  const rawFooterText = input.dressing.footerText;
  const footerText =
    rawFooterText !== null && rawFooterText.trim().length > 0 ? rawFooterText : null;
  const footerEnabled = documentFooterEnabled(input.dressing);

  // Transposed structure (spec R2/P2 Dossier): paliers as rows, one block per
  // dimension. This is a wholly different content layout from the tiled grid,
  // so it bypasses `input.tiles` entirely — that field was built by the
  // 'colonnes'-structure tiling and has no meaning here.
  const isTransposed = input.table.structure === 'transpose';
  const transposedSections: TransposedSection[] = isTransposed
    ? buildTransposedSections({
        model: input.model,
        fontSizePt: input.fontSizePt,
        density: input.table.density,
        contentWidthPx,
      })
    : [];
  // The page budget comes from the one derivation `planPdfDocument` uses too
  // (`transposedUsableHeightPx`), so the viewer's page count is this one.
  const transposedPages = isTransposed
    ? computeTransposedPages(
        transposedSections,
        transposedUsableHeightPx({
          geometry: input.geometry,
          documentHeaderHeightPx: headerHeightPx,
          subjectDescriptionIncluded: input.subjectDescription?.include ?? false,
          reservedFooterHeightPx: footerEnabled ? FOOTER_RESERVED_PX : 0,
        }),
      )
    : [];

  const gridPageCount = isTransposed ? transposedPages.length : input.tiles.length;
  // The notes pages follow the grid's. Planned here with the very function the
  // route runs for the render facts, so the page count agrees.
  const notesPages =
    input.notes === null || input.notes === undefined
      ? []
      : planNotesPages({
          section: input.notes,
          geometry: input.geometry,
          fontSizePt: input.fontSizePt,
          footerEnabled,
        });
  const totalPages = gridPageCount + notesPages.length;
  const tileKeys = isTransposed
    ? new Set(transposedPages.map((_, pageIndex) => `${pageIndex}:0`))
    : new Set(input.tiles.map((tile) => `${tile.rowPageIndex}:${tile.columnPageIndex}`));

  const context: DocumentContext = {
    layers: buildAnnotationLayers(input),
    headerHeightPx,
    headerHtml: renderDocumentHeader(input, headerHeightPx),
    tileKeys,
    totalPages,
    gridPageCount,
    signatureInCorner,
    footerEnabled,
    footerText,
  };

  const style = [
    // Decision F2: faces first — a rule referencing the family before its
    // @font-face is declared still works in CSS, but keeping the declaration
    // at the top keeps the emitted stylesheet readable when debugging a render.
    input.fontFaceCss ?? '',
    (input.subjectDescription?.include ?? false) ? SUBJECT_DESCRIPTION_STYLE : '',
    baseStyle(input, context),
    notesPages.length > 0 ? notesStyle(input.fontSizePt) : '',
  ]
    .filter((part) => part.length > 0)
    .join(' ');

  const body = isTransposed
    ? transposedPages
        .map((page, pageIndex) =>
          renderTransposedPage(input, transposedSections, page, pageIndex, context),
        )
        .join('')
    : input.tiles.map((tile, tileIndex) => renderTile(input, tile, tileIndex, context)).join('');
  const notesBody = notesPages
    .map((blocks, notesPageIndex) =>
      renderNotesPage(input, blocks, notesPageIndex, gridPageCount + notesPageIndex, context),
    )
    .join('');

  return renderHtmlDocument(input.matrixTitle, style, body + notesBody);
}
